import { Component, Vue } from 'vue-property-decorator';
import ParticipantService from '@/services/participant.service';
import TokenService from '@/services/token.service';
import AuthenticationError from '@/exceptions/authentication.error';
import { store, mutations } from '@/store/store';
import InputMaskedText from '@/components/inputs/InputMaskedText/InputMaskedText';
import InputNumber from '@/components/inputs/InputNumber/InputNumber';
import InputDate from '@/components/inputs/InputDate/InputDate';
import moment from 'moment';
import { IdentificationDTO } from '@/models/IdentificationDTO';
import ModalTemplate from '@/components/ModalTemplate/ModalTemplate';
import UserService from '@/services/user.service';
import LocalStoreService from '@/store/local.store';
import { isColonoscopyModeActivatedFromSU } from '@/config/app.config';
import { InclusionTypes } from '@/models/VariousEnum';
import { RawLocation } from 'vue-router';
import InformationBlock from '@/components/InformationBlock/InformationBlock';
// tslint:disable-next-line: no-var-requires
const VTooltip = require('v-tooltip');
Vue.use(VTooltip);

@Component({
  components: {
    InputMaskedText,
    InputNumber,
    InputDate,
    ModalTemplate,
    InformationBlock,
  },
})
export default class ParticipantIdentification extends Vue {
  // Properties
  public errorMessage: string = '';
  private participantId?: number = 0;
  private dateOfBirth?: string = '';
  private wrongUrl: boolean = false;
  private showModal: boolean = false;
  private openedDossierId?: number = 0;
  private showErrInfoModal?: boolean = false;

  public get errMsg(): string {
    return store.state.popUpErrMessage;
  }

  public get isInfoBlockEmpty(): boolean {
    const translation = this.$t('loginPage.infosParticipants').toString();
    return (translation === '' || translation === 'loginPage.infosParticipants') ? true : false;
  }

  public get isAuth(): boolean {
    return store.state.isAuthenticated;
  }

  // Lifecycle hook
  public mounted() {
    this.participantId = undefined;
    this.dateOfBirth = undefined;

    // Reset values from store related to coloscopy mode (access via URL)
    mutations.setInclusionType('');
    mutations.setIsDirectRefusal(false);
    this.checkColoModuleActivation();
  }

  // Methods
  /**
   * Logs the participant in.
   */
  protected async login() {
    // if colo module activated then set inclusion type
    if (store.state.isColonoscopyMode && store.state.inclusionType === '') {
      const inclusionType = this.$route.path.substring(this.$route.path.lastIndexOf('/') + 1);
      switch (inclusionType) {
        case InclusionTypes.fit:
          mutations.setInclusionType(InclusionTypes.fit);
          break;
        case InclusionTypes.colo:
          mutations.setInclusionType(InclusionTypes.colo);
          break;
        case InclusionTypes.refusal:
          mutations.setInclusionType(InclusionTypes.refusal);
          mutations.setIsDirectRefusal(true);
          break;
        // if url does not include inclusion type then ask user to redirect to correct url
        default:
          this.wrongUrl = true;
          return;
      }
    }

    // set maVisitedStepNumber to 0 in order to avoid stepping unchecked step
    this.$emit('openingDossier');
    // if a custom consent exists then set store value to true
    mutations.setConsentAccepted(false);
    mutations.setHasCustomConsent(!(this.$t('consentPage.consentText') === 'consentPage.consentText' || this.$t('consentPage.consentText') === ''));
    const partId = Number(this.participantId!);
    const dateOfBirth = new Date(moment(this.dateOfBirth!, 'DD.MM.YYYY').format('YYYY-MM-DD'));
    this.errorMessage = '';

    const loginResponse = await ParticipantService.login(
      partId, dateOfBirth, this.$route.params.screening)
      .then((data: IdentificationDTO) => {
        if (TokenService.tokenExists()) {
          this.$emit('identified', true);
          mutations.setIsIdentified(true);
          return data.suName;
        }
      })
      .catch((error: AuthenticationError) => {
        this.errorMessage = error.message;
        this.showErrInfoModal = store.state.showErrInfoModal;
      });

    if (loginResponse !== undefined && loginResponse !== this.$route.params.screening) {
      UserService.logoutFromFrontend();
      this.$emit('identified', false);
      mutations.setIsIdentified(false);
      this.$router.push({
        name: 'home',
        params: {
          screening: loginResponse,
        },
      }).then(() => {
        mutations.setInclusionType('');
        this.checkColoModuleActivation();
        this.login();
      });
    } else {
      if (loginResponse !== undefined && store.state.isIdentified) {
        await TokenService.generateXsrfToken()
          .then((data: any) => {
            TokenService.setXsrfTokenInHeader();

            // By default, the participant is redirected to the initialChoice page
            const nextLocation: RawLocation = {
              name: 'initialChoice',
              params: {
                screening: loginResponse,
                partId: String(partId),
              },
            };

            // In case of coloscopy mode, we adapt the route to bypass the initialChoice
            if (store.state.isColonoscopyMode) {
              nextLocation.name = store.state.isDirectRefusal ? 'alternativeQuestionnaire' : 'inclusion';
            }

            this.$router.push(nextLocation);
          });
      }
    }
  }

  /**
   * Opens a participant dossier
   */
  protected async openDossier() {
    // Make sure to reset theses values in case of a pro opening a dossier
    mutations.setInclusionType('');
    mutations.setIsColonoscopyMode(false);
    mutations.setIsDirectRefusal(false);
    mutations.setHasCustomConsent(false);

    const partId = Number(this.participantId!);
    const dateOfBirth = new Date(moment(this.dateOfBirth!, 'DD.MM.YYYY').format('YYYY-MM-DD'));
    this.errorMessage = '';

    // Check if we already have a dossier opened
    if (LocalStoreService.itemExists(LocalStoreService.localItemKeys.dossierId)) {
      const openedDossier = LocalStoreService.getItem(LocalStoreService.localItemKeys.dossierId);
      // Check that the opened dossier is different from the one we want to open
      if (Number(openedDossier) !== partId) {
        // Show alert modal and stop here
        this.openedDossierId = Number(openedDossier);
        this.showModal = true;
        return;
      }
    }

    // set maVisitedStepNumber to 0 in order to avoid stepping unchecked step
    this.$emit('openingDossier');

    // make sure to clear any pro data before opening a dossier
    // (in case it was not completly cleared in Confirmation step)
    UserService.clearProData();
    await ParticipantService.openDossier(partId, dateOfBirth)
    .then((response: IdentificationDTO) => {
        this.$router.push({
          name: 'inclusion',
          params: {
            screening: response.suName,
            partId: String(partId),
          },
        });
      })
      .catch((error: AuthenticationError) => {
        this.errorMessage = error.message;
        this.showErrInfoModal = store.state.showErrInfoModal;
    });
  }

  /**
   * Open dossier depending on modal response from user and adapt frontend accordingly.
   * @param {boolean} openNew If true, opens the new dossier (the one in the form).
   * If false, opens the old opened dossier (the one in localstorage).
   */
  protected async openDossierFromModal(openNew: boolean) {
    this.showModal = false;

    if (!openNew) {
      this.participantId = Number(LocalStoreService.getItem(LocalStoreService.localItemKeys.dossierId));
      this.dateOfBirth = undefined;
      return;
    }

    // set maVisitedStepNumber to 0 in order to avoid stepping unchecked step
    this.$emit('openingDossier');

    // We only replace the value of dossier here.
    // If we remove it from local storage, after successfully opening
    // a new dossier, it won't create it again.
    LocalStoreService.setItem(LocalStoreService.localItemKeys.dossierId, this.participantId!.toString());
    UserService.clearProData();
    await this.openDossier();
  }

  /**
   * Check if the colo module is activated and set value to the store
   * If it is not activated and the route does not end with 'login' then redirect to login page
   */
  protected checkColoModuleActivation() {
    mutations.setIsColonoscopyMode(isColonoscopyModeActivatedFromSU(this.$route.params.screening));
    // if colo mode activated and wrong url then redirect to login
    if (!store.state.isColonoscopyMode
      && this.$route.path.substring(this.$route.path.lastIndexOf('/') + 1) !== 'login') {
      // url not correct --> redirect to normal login page
      this.$router.push({
        name: 'login',
        params: {
          screening: this.$route.params.screening,
        },
      });
    }
  }

  protected setInclusionType(type: string) {
    mutations.setInclusionType(type);
  }

  protected hideModal() {
    this.showModal = false;
  }

  protected hideErrInfoModal() {
    mutations.setShowErrInfoModal(false);
    mutations.setPopUpErrMessage('');
    this.showErrInfoModal = store.state.showErrInfoModal;
  }

  protected hideWrongUrl() {
    this.wrongUrl = false;
  }

  /**
   * In case url did not give information about inclusion type,
   * Participant is asked about it and set inclusion type in store
   */
  protected fitInclusionRedirect() {
    mutations.setInclusionType(InclusionTypes.fit);
    this.wrongUrl = false;
    this.login();
  }

  /**
   * In case url did not give information about inclusion type,
   * Participant is asked about it and set inclusion type in store
   */
  protected coloInclusionRedirect() {
    mutations.setInclusionType(InclusionTypes.colo);
    this.wrongUrl = false;
    this.login();
  }

  /**
   * In case url did not give information about inclusion type,
   * Participant is asked about it and set inclusion type in store
   */
  protected refusalRedirect() {
    mutations.setInclusionType(InclusionTypes.refusal);
    mutations.setIsDirectRefusal(true);
    this.wrongUrl = false;
    this.login();
  }
}
